/**
 * Color palettes. A palette swaps the four player-identity colors:
 * host, guest, puck, bg. Everything else (text, panel chrome, glow
 * alphas) derives from these or stays stable across palettes.
 *
 * Selection happens once at page load — each refresh rolls a different
 * one. Both peers in a multiplayer match see their own independent
 * palette; from each player's POV the "your color" mapping stays
 * consistent via role, so it doesn't actually cause confusion.
 */

export interface Palette {
  id: string;
  name: string;
  host: string;
  guest: string;
  puck: string;
  bg: string;
}

export const PALETTES: readonly Palette[] = [
  {
    id: 'synthwave',
    name: 'Synthwave',
    host: '#ff2bd6',
    guest: '#22e7ff',
    puck: '#fff7a8',
    bg: '#08020e',
  },
  {
    id: 'inferno',
    name: 'Inferno',
    host: '#ff3a3a',
    guest: '#ffae3c',
    puck: '#ffec99',
    bg: '#100403',
  },
  {
    id: 'matrix',
    name: 'Matrix',
    host: '#2eff7c',
    guest: '#5eaaff',
    puck: '#ffffff',
    bg: '#021006',
  },
  {
    id: 'cosmic',
    name: 'Cosmic',
    host: '#b78aff',
    guest: '#ff80c8',
    puck: '#ffeed7',
    bg: '#0a0218',
  },
];

/** Picks a palette, biased away from the last one used (stored in
 *  sessionStorage) so consecutive refreshes don't repeat. */
export function pickRandomPalette(): Palette {
  const last = sessionStorage.getItem('hp-last-palette');
  const pool = last ? PALETTES.filter((p) => p.id !== last) : PALETTES;
  const p = pool[Math.floor(Math.random() * pool.length)]!;
  sessionStorage.setItem('hp-last-palette', p.id);
  return p;
}

/** Pushes the palette's colors into CSS variables on :root so any rule
 *  using var(--host), var(--guest), var(--puck), var(--bg) picks them
 *  up live. CSS variables are inherited everywhere. */
export function applyCssPalette(p: Palette): void {
  const root = document.documentElement;
  root.style.setProperty('--host', p.host);
  root.style.setProperty('--guest', p.guest);
  root.style.setProperty('--puck', p.puck);
  root.style.setProperty('--bg', p.bg);
}
